/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ChronoBusy — the chrono, over the screen, while something is being written.
 *
 * WHY THIS EXISTS. Attaching runs and importing a results file both used to
 * say nothing but a button whose label changed to "Attaching…" / "Importing…".
 * On a race of any size that write takes tens of seconds, and a greyed button
 * is the weakest signal a screen can give: nothing says the work is still
 * going, nothing says how far it has got, and nothing stops a second click
 * landing somewhere else on the panel while rows are being written.
 *
 * THE SAME STOPWATCH AS THE BOARD. The dial comes from BoardLoader via
 * ChronoDial, so the waiting state an athlete sees on the board and the one an
 * organizer sees mid-import are one mark, not two. Only the WORDS differ, and
 * they are the caller's to supply — "Counting everybody in" is the board
 * reading; this is a write, and it says so.
 *
 * IT COVERS THE SCREEN ON PURPOSE. These writes are not cancellable half way
 * (importApi chunks straight into the batch endpoint), so the honest thing is
 * to take the panel out of reach until it finishes rather than leave controls
 * live that must not be touched. `inert` is not used — support is uneven — the
 * scrim does the work.
 *
 * NOT A PROGRESS BAR WHEN THERE IS NO PROGRESS. `progress` is optional, and a
 * caller that cannot say where it has got to passes none and gets the dial
 * alone. Both of this file's callers CAN say: the import drives its own chunks,
 * and attachMany writes in batches of ATTACH_BATCH and reports each one. The
 * attach was measured at 73 seconds for 500 runs while a batch was 25 rows; it
 * is far quicker since the batch became 250, but a big field still takes long
 * enough that a bare dial would not do.
 */

import React, { useEffect } from 'react';
import {
  ChronoDial, CHRONO_CSS, INK, MUTED, ACCENT, HAIR,
} from './BoardLoader';

/**
 * @param open      whether to draw at all
 * @param title     what is happening, in the organizer's words. No ellipsis —
 *                  this adds it, the same way the board's loader does.
 * @param note      the second line: what it is doing and what not to do
 * @param progress  optional { done, total } — draws a bar and "x of y"
 */
export default function ChronoBusy({
  open,
  title = 'Working',
  note = 'This can take a moment. Please leave this page open.',
  progress = null,
}) {
  // The page behind must not scroll while the scrim is up: on a phone the
  // overlay is fixed and the panel underneath would otherwise slide around
  // beneath it, which reads as the page having come apart.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open) return null;

  const total = Number(progress?.total) || 0;
  const done = Math.min(Number(progress?.done) || 0, total);
  // Guarded: a total of zero would make this NaN and the bar would vanish
  // mid-write, which looks like a failure rather than a start.
  const pct = total > 0 ? Math.round((done / total) * 100) : null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        background: 'rgba(36,23,8,.46)',
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
      }}
    >
      <style>{CHRONO_CSS}</style>

      <div
        style={{
          width: '100%', maxWidth: 380, background: '#fff',
          border: `1px solid ${HAIR}`, borderRadius: 22,
          padding: '30px 26px 28px', textAlign: 'center',
          boxShadow: '0 30px 60px -28px rgba(36,23,8,.55)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        }}
      >
        <ChronoDial size={76} />

        <div>
          <p style={{ margin: 0, fontSize: 19, fontWeight: 700, color: INK, letterSpacing: '-.012em' }}>
            {title}&hellip;
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5, color: MUTED }}>
            {note}
          </p>
        </div>

        {pct !== null && (
          <div style={{ width: '100%' }}>
            <div
              style={{
                height: 6, borderRadius: 999, overflow: 'hidden',
                background: 'rgba(36,23,8,.09)',
              }}
            >
              <div
                style={{
                  height: '100%', width: `${pct}%`, background: ACCENT,
                  borderRadius: 999, transition: 'width .25s ease',
                }}
              />
            </div>
            <p
              style={{
                margin: '9px 0 0', fontSize: 12, fontWeight: 600, color: MUTED,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {done.toLocaleString()} of {total.toLocaleString()} &middot; {pct}%
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
